/**
 * The surface the symmetry guide draws on.
 *
 * `symmetry-toggle.ts` has been able to draw the mirror axis and the paired
 * region since Phase 3, and has never once drawn them, because nobody gave it a
 * canvas. That is the worst kind of unfinished: the code reviews as done, the
 * tests pass, and the feature does not exist.
 *
 * This is the missing half — a canvas laid exactly over the template, and the
 * hover tracking that says which face the pointer is on.
 *
 * **It draws nothing of its own.** It owns the surface, the sizing and the
 * hover; what appears is entirely the toggle's business. Splitting it that way
 * keeps the drawing next to the thing that knows what the pairing means, and
 * leaves this file with one job that is easy to check: is the overlay in the
 * same place as the canvas, at every window size.
 *
 * Spec: docs/decisions/mirror-and-editor-tools.md §3.
 */

import { CANVAS, REGION_IDS, type RegionId } from "../../shared/format";
import { REGIONS } from "../../shared/regions";

export interface SymmetryOverlayOptions {
  /** The paint canvas. The overlay tracks its box and its coordinate space. */
  canvas: HTMLCanvasElement;
  /** Draws the guide. Called with the face under the pointer, or null. */
  draw: (ctx: CanvasRenderingContext2D, hovered: RegionId | null) => void;
  doc?: Document;
}

export interface SymmetryOverlay {
  /** Redraw now — after the mode changed, or the body, or the garment. */
  refresh(): void;
  destroy(): void;
}

const STYLE_ID = "sym-overlay-css";

const CSS = `
/*
  Absolutely positioned and measured against the canvas, rather than placed in
  the stage's grid.

  The stage is a one-cell grid holding the canvas. Adding a second child would
  either take a new column — pushing the template off centre — or need an
  explicit grid-area, which changes how the canvas itself is placed. Measuring
  costs a ResizeObserver and touches nothing that already works.

  pointer-events: none is not optional. This sits directly over the drawing
  surface, and a guide that swallows the first millimetre of every stroke would
  be worse than no guide.
*/
.sym-overlay {
  position: absolute;
  pointer-events: none;
  z-index: 2;
}
`;

/** Which face a template pixel is on, or null for the dead space between. */
function regionAt(x: number, y: number): RegionId | null {
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return id;
  }
  return null;
}

export function mountSymmetryOverlay(opts: SymmetryOverlayOptions): SymmetryOverlay {
  const doc = opts.doc ?? document;
  const stage = opts.canvas.parentElement;
  if (!stage) throw new Error("symmetry overlay: the canvas has no stage to sit in");

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  /*
    The overlay measures itself against `offsetLeft`/`offsetTop`, which are
    relative to the nearest POSITIONED ancestor. The stage is one today, in a
    stylesheet this file does not own — and if that ever changes the offsets
    would silently start being relative to something further up and the guide
    would appear somewhere else on the page. Same guard, same reason, as
    `mode-chips.ts`.
  */
  if (getComputedStyle(stage).position === "static") stage.style.position = "relative";

  const canvas = doc.createElement("canvas");
  canvas.className = "sym-overlay";
  canvas.setAttribute("aria-hidden", "true");
  // Template space, not screen space, so the guide can be drawn in the same
  // coordinates the regions are declared in. CSS does the scaling.
  canvas.width = CANVAS.width;
  canvas.height = CANVAS.height;
  stage.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  let hovered: RegionId | null = null;

  /**
   * Put the overlay exactly where the canvas is.
   *
   * The canvas is letterboxed by CSS — `inline-size: auto` inside a centred
   * grid cell — so its drawn box is not the stage's box and cannot be assumed
   * from either. `offsetLeft`/`offsetTop` are relative to the stage, which is
   * `position: relative`, so they are already the numbers wanted here.
   */
  function place() {
    canvas.style.left = `${opts.canvas.offsetLeft}px`;
    canvas.style.top = `${opts.canvas.offsetTop}px`;
    canvas.style.width = `${opts.canvas.offsetWidth}px`;
    canvas.style.height = `${opts.canvas.offsetHeight}px`;
  }

  function refresh() {
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    opts.draw(ctx, hovered);
  }

  const observer = new ResizeObserver(() => {
    place();
    refresh();
  });
  observer.observe(opts.canvas);

  /**
   * Pointer to template pixels.
   *
   * The same straight ratio the painter uses: the canvas's bounding rect is its
   * drawn area, so there is no letterbox offset to subtract. Duplicated rather
   * than shared because the painter's copy is three lines inside a closure in
   * somebody else's file, and importing it would couple the guide's lifetime to
   * the painter's.
   */
  function onMove(e: PointerEvent) {
    const box = opts.canvas.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    const x = Math.floor(((e.clientX - box.left) / box.width) * CANVAS.width);
    const y = Math.floor(((e.clientY - box.top) / box.height) * CANVAS.height);
    const next = regionAt(x, y);
    // Only on a change of face. A pointermove fires per frame and the guide is
    // per region, so redrawing on every event would be ~60 pointless clears a
    // second during a stroke.
    if (next === hovered) return;
    hovered = next;
    refresh();
  }

  function onLeave() {
    if (hovered === null) return;
    hovered = null;
    refresh();
  }

  opts.canvas.addEventListener("pointermove", onMove);
  opts.canvas.addEventListener("pointerleave", onLeave);

  place();
  refresh();

  return {
    refresh,
    destroy() {
      observer.disconnect();
      opts.canvas.removeEventListener("pointermove", onMove);
      opts.canvas.removeEventListener("pointerleave", onLeave);
      canvas.remove();
    },
  };
}
